import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, User, LogOut, Sun, Moon, Facebook, Instagram, Home, Store, BadgePercent, Info, MessageCircle, ChevronDown, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import NotificationBell from '@/components/NotificationBell';
import BrandLogo from '@/components/BrandLogo';
import MobileMenu from '@/components/MobileMenu';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { useTheme } from '@/contexts/ThemeContext';
import { NAV } from '@/constants/testIds';
import { useSettings } from '@/contexts/SettingsContext';

const NAV_LINKS = [
  { to: '/', label: 'الرئيسية', icon: Home, testId: NAV.homeLink },
  { to: '/products', label: 'المنتجات', icon: Store, testId: NAV.productsLink },
  { to: '/offers', label: 'العروض', icon: BadgePercent, testId: NAV.offersLink },
  { to: '/about', label: 'من نحن', icon: Info, testId: NAV.aboutLink },
  { to: '/contact', label: 'تواصل معنا', icon: MessageCircle, testId: NAV.contactLink },
];

const Navbar = () => {
  const site = useSettings();
  const { user, logout } = useAuth();
  const { cartCount, openCart } = useCart();
  const { wishlistCount } = useWishlist();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const userName = user?.full_name || user?.name || 'حسابي';
  const userInitial = userName.trim().charAt(0).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="glass-nav sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
        <div className="hidden md:flex items-center justify-between h-10 border-b border-gray-200/50 dark:border-white/10 text-xs">
          <div className="flex items-center gap-3">
            {site.facebook && (
              <a 
                href={site.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-gray-600 hover:text-burgundy-500 transition-colors dark:text-gray-400"
                data-testid="topbar-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}
            {site.instagram && (
              <a 
                href={site.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-gray-600 hover:text-burgundy-500 transition-colors dark:text-gray-400"
                data-testid="topbar-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {site.whatsapp_url && (
              <a 
                href={site.whatsapp_url} 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="text-gray-600 hover:text-green-500 transition-colors dark:text-gray-400"
                data-testid="topbar-whatsapp"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
              </a>
            )}
            {site.whatsapp_display && (
              <a
                href={site.tel_url}
                aria-label="اتصال"
                className="inline-flex items-center gap-1.5 text-gray-600 hover:text-burgundy-500 dark:text-gray-400 transition-colors font-cairo mr-2"
                data-testid="topbar-phone"
              >
                <Phone className="w-4 h-4" />
                <span dir="ltr">{site.whatsapp_display}</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="flex items-center gap-1 text-gray-600 hover:text-burgundy-500 transition-colors dark:text-gray-400 font-cairo"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <>
                  <Moon className="w-4 h-4" />
                  <span>وضع ليلي</span>
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4" />
                  <span>وضع نهاري</span>
                </>
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-1 min-w-0">
            <MobileMenu links={NAV_LINKS} />
            <Link
              to="/"
              data-testid={NAV.logo}
              className="flex items-center gap-3 min-w-0 hover:opacity-90 transition-opacity cursor-pointer"
              title={site.site_name}
            >
              <BrandLogo imgClassName="h-11 md:h-16 w-auto" textClassName="text-xl sm:text-2xl md:text-3xl whitespace-nowrap" />
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {NAV_LINKS.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                data-testid={testId}
                className={({ isActive }) =>
                  `group inline-flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full font-cairo font-medium transition-colors duration-200 ${
                    isActive
                      ? 'bg-burgundy-500/10 text-burgundy-500'
                      : 'text-brand-black hover:text-burgundy-500 hover:bg-burgundy-500/5'
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px] transition-transform duration-200 group-hover:scale-110" strokeWidth={1.8} />
                {label}
              </NavLink>
            ))}
          </div>


          {/* على الموبايل: الوضع الليلي والدخول والحساب جوّا قائمة الهمبرغر */}
          <div className="flex items-center gap-1 md:gap-3 shrink-0">

            <Link
              to="/wishlist"
              aria-label="المفضلة"
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <Heart className="w-6 h-6 text-brand-black" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-burgundy-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <button
              data-testid={NAV.cartBtn}
              onClick={openCart}
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <ShoppingBag className="w-6 h-6 text-brand-black" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-burgundy-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>

            {user && <NotificationBell />}

            {user ? (
              <DropdownMenu dir="rtl">
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="hidden md:flex items-center gap-2 rounded-full p-1 md:pl-3 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-burgundy-500"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-burgundy-500 text-white font-tajawal font-bold text-sm">
                        {userInitial}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block max-w-[120px] truncate font-cairo text-sm font-semibold text-brand-black">
                      {userName}
                    </span>
                    <ChevronDown className="hidden md:block w-4 h-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 font-cairo">
                  <DropdownMenuLabel className="font-normal">
                    <p className="truncate font-semibold text-brand-black">{userName}</p>
                    {user.email && <p className="truncate text-xs text-gray-500">{user.email}</p>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid={NAV.accountBtn}
                    onSelect={() => navigate('/account')}
                    className="gap-2 cursor-pointer"
                  >
                    <User className="w-4 h-4" />
                    حسابي
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid={NAV.logoutBtn}
                    onSelect={handleLogout}
                    className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="w-4 h-4" />
                    تسجيل الخروج
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid={NAV.loginBtn}
                onClick={() => navigate('/login')}
                className="hidden md:inline-flex bg-burgundy-500 hover:bg-burgundy-600 text-white"
              >
                تسجيل الدخول
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
